// src/components/ElectionsData.js
import React, { useState, useMemo } from 'react';
import { 
  houseResults2024, 
  formatDistrict as formatDistrict2024, 
  getWinnerInfo as getHouseWinnerInfo2024, 
  stateNames 
} from '../lib/houseData2024';
import { 
  houseResults2022, 
  formatDistrict as formatDistrict2022, 
  getWinnerInfo as getHouseWinnerInfo2022 
} from '../lib/houseData2022';
import { 
  senateResults2024, 
  getWinnerInfo as getSenateWinnerInfo, 
  getMargin as getSenateMargin 
} from '../lib/senateData2024';

const ElectionsData = () => {
  const [selectedYear, setSelectedYear] = useState('2024');
  const [activeSubTab, setActiveSubTab] = useState('house');
  const [stateFilter, setStateFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedState, setExpandedState] = useState(null);

  // Dynamic data based on selected year
  const houseResults = selectedYear === '2024' ? houseResults2024 : houseResults2022;
  const formatDistrict = selectedYear === '2024' ? formatDistrict2024 : formatDistrict2022;
  const getHouseWinnerInfo = selectedYear === '2024' ? getHouseWinnerInfo2024 : getHouseWinnerInfo2022;

  // House: Get unique states for dropdown
  const houseStates = useMemo(() => {
    const uniqueStates = [...new Set(houseResults.map(r => r.state))].sort();
    return uniqueStates;
  }, [houseResults]);

  // Senate: Get unique states for dropdown
  const senateStates = useMemo(() => {
    return senateResults2024.map(r => r.state).sort();
  }, []);

  // House: Filter races
  const filteredHouseRaces = useMemo(() => {
    return houseResults.filter(race => {
      const matchesState = stateFilter === 'All' || race.state === stateFilter;
      const matchesSearch = searchTerm === '' || 
        (race.demCandidate && race.demCandidate.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (race.repCandidate && race.repCandidate.toLowerCase().includes(searchTerm.toLowerCase())) ||
        race.state.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesState && matchesSearch;
    });
  }, [houseResults, stateFilter, searchTerm]);

  // Senate: Filter races
  const filteredSenateRaces = useMemo(() => {
    return senateResults2024.filter(race => {
      const matchesState = stateFilter === 'All' || race.state === stateFilter;
      const stateName = stateNames[race.state] || '';
      const matchesSearch = searchTerm === '' || 
        (race.demCandidate && race.demCandidate.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (race.repCandidate && race.repCandidate.toLowerCase().includes(searchTerm.toLowerCase())) ||
        race.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stateName.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesState && matchesSearch;
    });
  }, [stateFilter, searchTerm]);

  // House: Calculate totals
  const houseTotals = useMemo(() => {
    return filteredHouseRaces.reduce((acc, race) => {
      acc.demVotes += race.demVotes;
      acc.repVotes += race.repVotes;
      acc.totalVotes += race.totalVotes;
      acc.demSeats += race.winner === 'D' ? 1 : 0;
      acc.repSeats += race.winner === 'R' ? 1 : 0;
      return acc;
    }, { demVotes: 0, repVotes: 0, totalVotes: 0, demSeats: 0, repSeats: 0 });
  }, [filteredHouseRaces]);

  // Senate: Calculate totals
  const senateTotals = useMemo(() => {
    return filteredSenateRaces.reduce((acc, race) => {
      acc.demVotes += race.demVotes;
      acc.repVotes += race.repVotes;
      acc.indVotes += race.indVotes || 0;
      acc.otherVotes += race.otherVotes;
      acc.totalVotes += race.totalVotes;
      acc.demSeats += race.winner === 'D' ? 1 : 0;
      acc.repSeats += race.winner === 'R' ? 1 : 0;
      acc.indSeats += race.winner === 'I' ? 1 : 0;
      return acc;
    }, { demVotes: 0, repVotes: 0, indVotes: 0, otherVotes: 0, totalVotes: 0, demSeats: 0, repSeats: 0, indSeats: 0 });
  }, [filteredSenateRaces]);

  const formatNumber = (num) => num.toLocaleString();

  const formatPct = (votes, total) => {
    if (total === 0) return '0.0%';
    return ((votes / total) * 100).toFixed(1) + '%';
  };

  const getHouseMargin = (race) => {
    const margin = race.demVotes - race.repVotes;
    const pct = ((Math.abs(margin) / race.totalVotes) * 100).toFixed(1);
    if (margin > 0) return { text: `D+${pct}%`, color: '#2563eb' };
    if (margin < 0) return { text: `R+${pct}%`, color: '#dc2626' };
    return { text: 'TIE', color: '#6b7280' };
  };

  // Reset filters when switching tabs
  const handleTabChange = (tab) => {
    setActiveSubTab(tab);
    setStateFilter('All');
    setSearchTerm('');
    setExpandedState(null);
  };

  // Handle year change
  const handleYearChange = (year) => {
    setSelectedYear(year);
    setStateFilter('All');
    setSearchTerm('');
    setExpandedState(null);
  };

  // Toggle county expansion
  const toggleExpand = (state) => {
    setExpandedState(expandedState === state ? null : state);
  };

  return (
    <div style={{ padding: '28px 32px' }}>
      {/* Header with Year Toggle */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, color: 'var(--color-text-primary)', letterSpacing: '-0.02em' }}>
            Election Results
          </h1>
          {/* Year Toggle */}
          <div style={{
            display: 'flex',
            backgroundColor: 'var(--color-surface-raised)',
            borderRadius: '8px',
            padding: '4px'
          }}>
            {['2024', '2022'].map(year => (
              <button
                key={year}
                onClick={() => handleYearChange(year)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: selectedYear === year ? '600' : '500',
                  color: selectedYear === year ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                  backgroundColor: selectedYear === year ? 'var(--color-surface)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  boxShadow: selectedYear === year ? 'var(--shadow-sm)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
        <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          {activeSubTab === 'house' 
            ? (stateFilter === 'All' ? 'All 435 House races' : `${stateNames[stateFilter]} House races`)
            : (stateFilter === 'All' ? `${filteredSenateRaces.length} Senate races` : `${stateNames[stateFilter]} Senate race`)
          }
        </p>
      </div>

      {/* Sub-tabs: House | Senate */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        <button
          onClick={() => handleTabChange('house')}
          style={{
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: activeSubTab === 'house' ? '600' : '500',
            color: activeSubTab === 'house' ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'house' ? '2px solid var(--color-accent-hover)' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer'
          }}
        >
          🏛️ House
        </button>
        <button
          onClick={() => handleTabChange('senate')}
          style={{
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: activeSubTab === 'senate' ? '600' : '500',
            color: activeSubTab === 'senate' ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'senate' ? '2px solid var(--color-accent-hover)' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer'
          }}
        >
          🏛️ Senate
        </button>
      </div>

      {/* ========== HOUSE TAB ========== */}
      {activeSubTab === 'house' && (
        <>
          {/* Summary Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px', 
            marginBottom: '24px' 
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              padding: '20px'
            }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Total Votes</p>
              <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                {formatNumber(houseTotals.totalVotes)}
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              padding: '20px',
              borderLeft: '4px solid #dc2626'
            }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Republican Votes</p>
              <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#dc2626', margin: 0 }}>
                {formatNumber(houseTotals.repVotes)}
              </p>
              <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0 0' }}>
                {formatPct(houseTotals.repVotes, houseTotals.totalVotes)} of total
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              padding: '20px',
              borderLeft: '4px solid #2563eb'
            }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Democrat Votes</p>
              <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#2563eb', margin: 0 }}>
                {formatNumber(houseTotals.demVotes)}
              </p>
              <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0 0' }}>
                {formatPct(houseTotals.demVotes, houseTotals.totalVotes)} of total
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              padding: '20px'
            }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Seats Won</p>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'baseline' }}>
                <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#dc2626' }}>
                  {houseTotals.repSeats} R
                </span>
                <span style={{ fontSize: '20px', color: '#6b7280' }}>-</span>
                <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#2563eb' }}>
                  {houseTotals.demSeats} D
                </span>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: 'var(--shadow-sm)',
            border: '1px solid var(--color-border)',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <input
              type="text"
              placeholder="Search by candidate or state..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: '250px',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                minWidth: '180px'
              }}
            >
              <option value="All">All States</option>
              {houseStates.map(st => (
                <option key={st} value={st}>{stateNames[st]} ({st})</option>
              ))}
            </select>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>
              Showing {filteredHouseRaces.length} races
            </span>
          </div>

          {/* House Results Table */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: 'var(--shadow-sm)',
            border: '1px solid var(--color-border)',
            overflow: 'hidden'
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-surface-raised)', borderBottom: '2px solid var(--color-border)' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-muted)' }}>District</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-muted)' }}>Winner</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>GOP Votes</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#2563eb' }}>Dem Votes</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: 'var(--color-text-muted)' }}>Total</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: 'var(--color-text-muted)' }}>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHouseRaces.map((race, idx) => {
                    const winner = getHouseWinnerInfo(race);
                    const margin = getHouseMargin(race);
                    return (
                      <tr 
                        key={`${race.state}-${race.district}`}
                        style={{ 
                          borderBottom: '1px solid #e5e7eb',
                          backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb'
                        }}
                      >
                        <td style={{ padding: '12px 16px', fontWeight: '500' }}>
                          {formatDistrict(race.state, race.district)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: race.winner === 'D' ? '#2563eb' : '#dc2626'
                            }} />
                            <span>{winner.name}</span>
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: race.winner === 'D' ? '#dbeafe' : '#fee2e2',
                              color: race.winner === 'D' ? '#1d4ed8' : '#b91c1c'
                            }}>
                              {winner.party === 'Democrat' ? 'D' : 'R'}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#dc2626' }}>
                          {formatNumber(race.repVotes)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#2563eb' }}>
                          {formatNumber(race.demVotes)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '500' }}>
                          {formatNumber(race.totalVotes)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: margin.color,
                            backgroundColor: race.winner === 'D' ? '#dbeafe' : '#fee2e2'
                          }}>
                            {margin.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ========== SENATE TAB ========== */}
      {activeSubTab === 'senate' && (
        <>
          {/* Notice for 2022 Senate data */}
          {selectedYear === '2022' && (
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>⚠️</span>
              <span style={{ color: '#92400e', fontSize: '14px' }}>
                2022 Senate data coming soon. Showing 2024 results below.
              </span>
            </div>
          )}

          {/* Summary Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px', 
            marginBottom: '24px' 
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              padding: '20px'
            }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Total Votes</p>
              <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                {formatNumber(senateTotals.totalVotes)}
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              padding: '20px',
              borderLeft: '4px solid #dc2626'
            }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Republican Votes</p>
              <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#dc2626', margin: 0 }}>
                {formatNumber(senateTotals.repVotes)}
              </p>
              <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0 0' }}>
                {formatPct(senateTotals.repVotes, senateTotals.totalVotes)} of total
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              padding: '20px',
              borderLeft: '4px solid #2563eb'
            }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Democrat Votes</p>
              <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#2563eb', margin: 0 }}>
                {formatNumber(senateTotals.demVotes)}
              </p>
              <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0 0' }}>
                {formatPct(senateTotals.demVotes, senateTotals.totalVotes)} of total
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              padding: '20px'
            }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Seats Won</p>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'baseline' }}>
                <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#dc2626' }}>
                  {senateTotals.repSeats} R
                </span>
                <span style={{ fontSize: '20px', color: '#6b7280' }}>-</span>
                <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#2563eb' }}>
                  {senateTotals.demSeats} D
                </span>
                {senateTotals.indSeats > 0 && (
                  <>
                    <span style={{ fontSize: '20px', color: '#6b7280' }}>-</span>
                    <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a' }}>
                      {senateTotals.indSeats} I
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: 'var(--shadow-sm)',
            border: '1px solid var(--color-border)',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <input
              type="text"
              placeholder="Search by candidate or state..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: '250px',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                minWidth: '180px'
              }}
            >
              <option value="All">All States</option>
              {senateStates.map(st => (
                <option key={st} value={st}>{stateNames[st]} ({st})</option>
              ))}
            </select>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>
              Showing {filteredSenateRaces.length} races
            </span>
          </div>

          {/* Senate Results Table with Expandable County Rows */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: 'var(--shadow-sm)',
            border: '1px solid var(--color-border)',
            overflow: 'hidden'
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-surface-raised)', borderBottom: '2px solid var(--color-border)' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '40px' }}></th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>State</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Winner</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>GOP Votes</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#2563eb' }}>Dem Votes</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#6b7280' }}>Other</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSenateRaces.map((race, idx) => {
                    const winner = getSenateWinnerInfo(race);
                    const margin = getSenateMargin(race);
                    const isExpanded = expandedState === race.state;
                    const partyColors = { D: '#2563eb', R: '#dc2626', I: '#16a34a' };
                    const partyBgColors = { D: '#dbeafe', R: '#fee2e2', I: '#dcfce7' };
                    
                    return (
                      <React.Fragment key={race.state}>
                        {/* Main State Row */}
                        <tr 
                          style={{ 
                            borderBottom: '1px solid #e5e7eb',
                            backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb',
                            cursor: 'pointer'
                          }}
                          onClick={() => toggleExpand(race.state)}
                        >
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <span style={{ 
                              display: 'inline-block',
                              transition: 'transform 0.2s',
                              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                            }}>
                              ▶
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontWeight: '500' }}>
                            {stateNames[race.state]}
                            {race.incumbent && (
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '11px', 
                                color: '#6b7280',
                                backgroundColor: '#f3f4f6',
                                padding: '2px 6px',
                                borderRadius: '4px'
                              }}>
                                Inc: {race.incumbent}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: partyColors[race.winner]
                              }} />
                              <span>{winner.name}</span>
                              <span style={{
                                fontSize: '11px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                backgroundColor: partyBgColors[race.winner],
                                color: partyColors[race.winner]
                              }}>
                                {race.winner}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: '#dc2626' }}>
                            {formatNumber(race.repVotes)}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: '#2563eb' }}>
                            {formatNumber(race.demVotes)}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: '#6b7280' }}>
                            {formatNumber((race.indVotes || 0) + race.otherVotes)}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '500' }}>
                            {formatNumber(race.totalVotes)}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: margin.color,
                              backgroundColor: partyBgColors[margin.party]
                            }}>
                              {margin.text}
                            </span>
                          </td>
                        </tr>

                        {/* Expanded County Rows */}
                        {isExpanded && race.counties && (
                          <tr>
                            <td colSpan={8} style={{ padding: 0, backgroundColor: '#f9fafb' }}>
                              <div style={{ 
                                padding: '16px 16px 16px 56px',
                                maxHeight: '400px',
                                overflowY: 'auto'
                              }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                  <thead>
                                    <tr style={{ backgroundColor: '#e5e7eb' }}>
                                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>County</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total Votes</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Margin</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {race.counties.map((county, cIdx) => (
                                      <tr 
                                        key={county.county}
                                        style={{ 
                                          borderBottom: '1px solid #e5e7eb',
                                          backgroundColor: cIdx % 2 === 0 ? 'white' : '#f9fafb'
                                        }}
                                      >
                                        <td style={{ padding: '8px 12px' }}>{county.county}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                          {formatNumber(county.totalVotes)}
                                        </td>
                                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                          <span style={{
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            color: county.marginParty === 'D' ? '#2563eb' : county.marginParty === 'R' ? '#dc2626' : '#16a34a',
                                            backgroundColor: county.marginParty === 'D' ? '#dbeafe' : county.marginParty === 'R' ? '#fee2e2' : '#dcfce7'
                                          }}>
                                            {county.marginParty}+{county.margin}%
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ElectionsData;